import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const ProjectsContext = createContext();
const API = (import.meta.env?.VITE_API_URL || '') + '/api/v1/projects';

// Fields that indicate full enrichment has run
const ENRICHMENT_KEYS = ['slope_pct', 'flood_zone', 'nearest_ixp_distance_km', 'nearest_road_distance_km', 'free_cooling_days_yr', 'water_distance_km'];
function needsEnrichment(project) {
  const e = project.enrichment || {};
  const missing = ENRICHMENT_KEYS.filter(k => e[k] == null).length;
  return missing >= 3;
}

export function ProjectsProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(new Set()); // keys being saved (lat_lon)
  const [enriching, setEnriching] = useState(new Set()); // project ids being enriched
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  // ── Auth0 bearer attach ────────────────────────────────────────────
  // Step 6-mini in PLAN_AUTH.md: projects.py is org-scoped at the backend,
  // so every fetch from this context needs Authorization: Bearer when the
  // user is logged in. When Auth0 isn't wired (local dev with empty
  // VITE_AUTH0_DOMAIN), useAuth0 returns isAuthenticated=false and we
  // fall through to anonymous fetch (which the backend rejects with 401
  // unless DEV_AUTH_BYPASS is on in that local env).
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const authedFetch = useCallback(async (url, init = {}) => {
    const headers = new Headers(init.headers || {});
    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently();
        headers.set('Authorization', `Bearer ${token}`);
      } catch {
        // Token refresh failed; let the server reject with 401 and the
        // existing error handling surface it. Don't crash here.
      }
    }
    return fetch(url, { ...init, headers });
  }, [isAuthenticated, getAccessTokenSilently]);

  // ── Fetch all projects ─────────────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    try {
      const resp = await authedFetch(API);
      if (resp.ok) {
        setProjects(await resp.json());
        setError(null);
      } else {
        setError(`API error ${resp.status}`);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [authedFetch]);

  useEffect(() => {
    // Wait for Auth0 to finish hydrating its session before the first fetch,
    // otherwise we race the isLoading -> isAuthenticated transition and the
    // page-load fetch goes out anonymous, getting 401 from the org-scoped
    // backend.
    if (authLoading) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchProjects();
  }, [authLoading, fetchProjects]);

  // ── Fetch a single project ─────────────────────────────────────────
  // Fresh fetch (not from the in-memory list) so deep-link refreshes on
  // #/projects/:id work even before the list has hydrated. Authed via
  // the same wrapper so the bearer token is attached.
  const getProject = useCallback(async (projectId) => {
    try {
      const resp = await authedFetch(`${API}/${projectId}`);
      if (resp.ok) return await resp.json();
      console.error(`getProject failed (status ${resp.status})`);
    } catch (err) {
      console.error('getProject failed:', err);
    }
    return null;
  }, [authedFetch]);

  // ── Save a site as a project ───────────────────────────────────────
  const saveProject = useCallback(async (siteData) => {
    const key = `${siteData.lat}_${siteData.lon}`;
    setSaving(prev => new Set(prev).add(key));
    setError(null);
    try {
      // Build clean enrichment — strip internal fields
      const enrichment = {};
      for (const [k, v] of Object.entries(siteData)) {
        if (k.startsWith('dd_') || k === '_enriched' || typeof v === 'function') continue;
        if (v !== undefined) enrichment[k] = v;
      }
      const resp = await authedFetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_label: siteData.dd_label || siteData.site_label || siteData.nearest_grid_name || `Site ${siteData.lat.toFixed(3)}, ${siteData.lon.toFixed(3)}`,
          lat: siteData.lat,
          lon: siteData.lon,
          // Default flipped FI → NO (audit 2026-05-28). FI default was
          // silently writing the wrong country onto Helgeland saves; the
          // re-enrich endpoint then ran FI-only lookups (grid_connections_fi)
          // and overwrote nearest_grid_* with a Finnish substation 600+ km
          // away. Call sites now stamp country from search context.
          country: siteData.country || 'NO',
          use_case: siteData.use_case || 'data_center',
          enrichment,
        }),
      });
      if (resp.ok) {
        const created = await resp.json();
        setProjects(prev => [created, ...prev]);

        // Auto-enrich in background if data is sparse
        if (needsEnrichment(created)) {
          const pid = created.id;
          setEnriching(prev => new Set(prev).add(pid));
          authedFetch(`${API}/${pid}/re-enrich`, { method: 'POST' })
            .then(r => r.ok ? r.json() : null)
            .then(updated => {
              if (updated) setProjects(prev => prev.map(p => p.id === pid ? updated : p));
            })
            .catch(() => {})
            .finally(() => setEnriching(prev => { const n = new Set(prev); n.delete(pid); return n; }));
        }

        return created;
      } else {
        const errText = await resp.text().catch(() => resp.statusText);
        setError(`Save failed (${resp.status}): ${errText.slice(0, 120)}`);
        return null;
      }
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      return null;
    } finally {
      setSaving(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, [authedFetch]);

  // ── Update a project ───────────────────────────────────────────────
  const updateProject = useCallback(async (projectId, updates) => {
    try {
      const resp = await authedFetch(`${API}/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (resp.ok) {
        const updated = await resp.json();
        setProjects(prev => prev.map(p => p.id === projectId ? updated : p));
        return updated;
      }
    } catch (err) {
      console.error('Update failed:', err);
    }
    return null;
  }, [authedFetch]);

  // ── Delete (soft) a project ────────────────────────────────────────
  const deleteProject = useCallback(async (projectId) => {
    try {
      const resp = await authedFetch(`${API}/${projectId}`, { method: 'DELETE' });
      if (resp.ok) {
        setProjects(prev => prev.map(p =>
          p.id === projectId ? { ...p, stage: 'dropped' } : p
        ));
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  }, [authedFetch]);

  // ── Re-enrich a project ────────────────────────────────────────────
  const reEnrich = useCallback(async (projectId) => {
    setEnriching(prev => new Set(prev).add(projectId));
    try {
      const resp = await authedFetch(`${API}/${projectId}/re-enrich`, { method: 'POST' });
      if (resp.ok) {
        const updated = await resp.json();
        setProjects(prev => prev.map(p => p.id === projectId ? updated : p));
        return updated;
      }
    } catch (err) {
      console.error('Re-enrich failed:', err);
    } finally {
      setEnriching(prev => { const n = new Set(prev); n.delete(projectId); return n; });
    }
    return null;
  }, [authedFetch]);

  const isEnriching = useCallback((projectId) => enriching.has(projectId), [enriching]);

  // ── Check if a location is already saved ───────────────────────────
  const isSaved = useCallback((lat, lon) => {
    return projects.some(p =>
      p.stage !== 'dropped' &&
      Math.abs(p.lat - lat) < 0.0001 && Math.abs(p.lon - lon) < 0.0001
    );
  }, [projects]);

  // Find the saved project at coords (returns the full project or null).
  // Lets the unsave-from-list flow find which project id to drop.
  const findSavedProject = useCallback((lat, lon) => {
    return projects.find(p =>
      p.stage !== 'dropped' &&
      Math.abs(p.lat - lat) < 0.0001 && Math.abs(p.lon - lon) < 0.0001
    ) || null;
  }, [projects]);

  const isSaving = useCallback((lat, lon) => saving.has(`${lat}_${lon}`), [saving]);

  // Derived counts
  const screeningProjects = projects.filter(p => p.stage === 'screening');
  const activeProjects = projects.filter(p => p.stage !== 'dropped');
  const projectCount = activeProjects.length;

  return (
    <ProjectsContext.Provider value={{
      projects, activeProjects, screeningProjects, projectCount,
      loading, error, saving: saving.size > 0,
      enrichingCount: enriching.size,
      fetchProjects, getProject, saveProject, updateProject, deleteProject, reEnrich,
      isSaved, findSavedProject, isSaving, isEnriching,
    }}>
      {children}
    </ProjectsContext.Provider>
  );
}

export const useProjects = () => useContext(ProjectsContext);
